import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";

import {
    Box,
    Button,
    Chip,
    Dialog,
    Divider,
    IconButton,
    Paper,
    Stack,
    Typography,
    CircularProgress,
    Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import RotateRightRoundedIcon from "@mui/icons-material/RotateRightRounded";
import { ds } from "../../../../styles/designSystem";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import LoadingDots from "../../../../component/common/LoadingDots";
import ActionButton from "../../../../component/common/ActionButton";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** ✅ ResizeObserver + rAF 재측정까지 포함한 안정 버전 */
function useResizeObserver(ref, enabled = true) {
    const [rect, setRect] = useState({ width: 0, height: 0 });

    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return false;
        const r = el.getBoundingClientRect();
        const w = Math.floor(r.width);
        const h = Math.floor(r.height);
        setRect({ width: w, height: h });
        return w > 0 && h > 0;
    }, [ref]);

    useEffect(() => {
        if (!enabled) return;

        let rafId = 0;
        let tries = 0;
        let ro;

        const loop = () => {
            tries += 1;
            const ok = measure();
            if (!ok && tries < 30) rafId = requestAnimationFrame(loop);
        };

        loop();

        const el = ref.current;
        if (el && "ResizeObserver" in window) {
            ro = new ResizeObserver(() => measure());
            ro.observe(el);
        } else {
            window.addEventListener("resize", measure);
        }

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", measure);
        };
    }, [enabled, measure, ref]);

    return { ...rect, measure };
}

/**
 * 페이지 한 장 셀 — React.memo 로 감싸 "자기 props 가 바뀐 페이지만" 재렌더.
 * react-window 리스트의 children(Row) 이 pageDim 변경으로 재생성돼도, 치수/scale 이 그대로인
 * 기존 페이지는 여기서 재렌더를 건너뛰어 깜빡임(재래스터)을 막는다.
 * scale 계산도 셀 안에서 하므로, 입력(dim/pageBox/rotate)이 같으면 결과가 동일 → memo 유지.
 */
const PdfPageCell = React.memo(function PdfPageCell({
    pdf, pageNumber, dim, pageBox, rotate, isMatched, onDim, highlight,
}) {
    const w0 = dim?.w || 595;
    const h0 = dim?.h || 842;
    const isSwap = rotate === 90 || rotate === 270;
    const rotW = isSwap ? h0 : w0;
    const rotH = isSwap ? w0 : h0;
    const innerPad = 16;
    const boxW = Math.max(200, pageBox.w - innerPad * 2);
    const boxH = Math.max(200, pageBox.h - innerPad * 2);
    const scale = Math.max(0.1, Math.min(Math.min(boxW / rotW, boxH / rotH), 8));

    return (
        <Box
            sx={{
                width: "100%", height: "100%",
                display: "flex", justifyContent: "center", alignItems: "center",
                py: 1.5, boxSizing: "border-box",
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: pageBox.w, height: pageBox.h,
                    borderRadius: 3,
                    borderWidth: isMatched ? 3 : 1,
                    borderColor: isMatched ? "primary.main" : "divider",
                    bgcolor: "#fff", overflow: "hidden",
                    display: "flex", justifyContent: "center", alignItems: "center", p: 1,
                }}
            >
                <Page
                    pdf={pdf}
                    pageNumber={pageNumber}
                    rotate={rotate}
                    scale={scale}
                    renderAnnotationLayer={false}
                    renderTextLayer
                    onGetTextLayerSuccess={highlight}
                    onLoadSuccess={(page) => {
                        const ow = page?.originalWidth;
                        const oh = page?.originalHeight;
                        if (ow && oh) onDim(pageNumber, ow, oh);
                    }}
                />
            </Paper>
        </Box>
    );
});

/**
 * 리스트 한 행 — **모듈 스코프의 안정된 컴포넌트 타입**.
 * react-window 는 children 을 컴포넌트 "타입"으로 렌더하므로, 이 타입이 매 렌더마다 바뀌면
 * (예: pageDim 이 든 useCallback) 아이템 타입 교체로 간주돼 화면의 모든 행이 unmount→remount 되고
 * PDF 캔버스가 통째로 재래스터된다. 타입을 고정하고 페이지별 데이터는 itemData(data)로 받아,
 * pageDim 이 바뀌어도 remount 없이 재렌더만 하고 → PdfPageCell 의 memo 가 안 바뀐 페이지를 건너뛴다.
 */
function PdfRow({ index, style, data }) {
    const { pdf, pageDim, pageBox, matchedPageSet, rotate, onDim, highlight } = data;
    const pageNumber = index + 1;
    return (
        <Box style={style} sx={{ width: "100%", height: "100%" }}>
            <PdfPageCell
                pdf={pdf}
                pageNumber={pageNumber}
                dim={pageDim[pageNumber]}
                pageBox={pageBox}
                rotate={rotate}
                isMatched={matchedPageSet.has(pageNumber)}
                onDim={onDim}
                highlight={highlight}
            />
        </Box>
    );
}

export default function PdfViewerModal({
                                           isOpen = false,
                                           onClose,
                                           path,
                                           keyword = "",
                                           matchedPages = [],
                                           initialPage,
                                       }) {
    const [pdf, setPdf] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [pageDim, setPageDim] = useState({});

    const viewerRef = useRef(null);
    const listRef = useRef(null);

    const { width: vw, height: vh, measure } = useResizeObserver(viewerRef, isOpen);

    // ✅ 회전 토글: 기본은 0 (안 건드림)
    const [rotateOverride, setRotateOverride] = useState(0); // 0 | 90 | 180 | 270

    const sortedMatchedPages = useMemo(
        () => [...(matchedPages || [])].sort((a, b) => a.page - b.page),
        [matchedPages]
    );

    const matchedPageSet = useMemo(
        () => new Set(sortedMatchedPages.map((p) => p.page)),
        [sortedMatchedPages]
    );

    const targetFirstPage = useMemo(() => {
        if (initialPage && Number(initialPage) >= 1) return Number(initialPage);
        if (sortedMatchedPages.length > 0) return sortedMatchedPages[0].page;
        return 1;
    }, [initialPage, sortedMatchedPages]);

    const onDocumentLoadSuccess = useCallback((pdfDoc) => {
        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages || 0);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setPdf(null);
        setNumPages(0);
        setPageDim({});
        setRotateOverride(0); // ✅ 모달 열 때 회전은 항상 기본값으로
    }, [isOpen, path]);

    useEffect(() => {
        if (!isOpen) return;
        if (!numPages || !listRef.current) return;
        const target = Math.min(Math.max(targetFirstPage, 1), numPages);
        listRef.current.scrollToItem(target - 1, "start");
    }, [isOpen, numPages, targetFirstPage]);

    const toggleRotate = useCallback(() => {
        setRotateOverride((prev) => {
            const next = (prev + 90) % 360;
            return next;
        });
        // ✅ 회전 변경 시 레이아웃 재측정만. (회전값은 itemData 로 흘러 전 페이지가 재렌더됨.
        //    resetAfterIndex 는 VariableSizeList 전용이라 FixedSizeList 에선 no-op → 제거)
        requestAnimationFrame(() => {
            measure();
        });
    }, [measure]);

    const highlightTextLayer = useCallback(
        (textLayerDiv) => {
            if (!keyword || !textLayerDiv) return;
            const escaped = String(keyword).replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
            const regex = new RegExp(`(${escaped})`, "gi");

            Array.from(textLayerDiv.querySelectorAll("span")).forEach((span) => {
                if (!span?.textContent) return;
                if (!regex.test(span.textContent)) return;
                span.innerHTML = span.textContent.replace(
                    regex,
                    `<mark style="background: rgba(255, 235, 59, 0.55); padding: 0 2px; border-radius: 4px;">$1</mark>`
                );
            });
        },
        [keyword]
    );

    // ✅ viewer 영역 기준 페이지 박스
    const pageBox = useMemo(() => {
        const safeX = 60;
        const safeY = 40;
        return {
            w: Math.max(320, (vw || 0) - safeX),
            h: Math.max(240, (vh || 0) - safeY),
        };
    }, [vw, vh]);

    const itemSize = useMemo(() => Math.max(240, vh || 600), [vh]);

    // 페이지 치수 저장 — 안정된 콜백(정체성 유지). 같은 값이면 setState 를 건너뛰어 불필요 렌더 방지.
    const handleDim = useCallback((pageNumber, ow, oh) => {
        setPageDim((prev) => {
            const cur = prev[pageNumber];
            if (cur?.w === ow && cur?.h === oh) return prev;
            return { ...prev, [pageNumber]: { w: ow, h: oh } };
        });
    }, []);

    // react-window 로 넘기는 페이지별 데이터. children(PdfRow) 타입은 고정하고, 변하는 값은 여기로만 흘린다.
    // pageDim 이 바뀌면 이 객체만 새로 생겨 리스트가 재렌더되지만, PdfRow 타입은 그대로라 remount 가 없다.
    const itemData = useMemo(
        () => ({
            pdf,
            pageDim,
            pageBox,
            matchedPageSet,
            rotate: rotateOverride,
            onDim: handleDim,
            highlight: highlightTextLayer,
        }),
        [pdf, pageDim, pageBox, matchedPageSet, rotateOverride, handleDim, highlightTextLayer]
    );

    const showStrip = Boolean(keyword);

    return (
        <Dialog
            open={Boolean(isOpen)}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            disablePortal={false}
            TransitionProps={{
                onEntered: () => measure(),
            }}
            PaperProps={{
                sx: {
                    width: "92vw",
                    height: "92vh",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            <Box
                sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: "#fff",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                }}
            >
                <Typography
                    noWrap
                    sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", minWidth: 0 }}
                >
                    PDF 미리보기
                </Typography>

                {/* 회전 상태 표시 */}
                <Chip
                    label={`회전 ${rotateOverride}°`}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 0.5, fontWeight: 700, borderRadius: 1, fontSize: 11 }}
                />

                <Box sx={{ flex: 1 }} />

                <ActionButton
                    variant="secondary"
                    startIcon={<RotateRightRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={toggleRotate}
                    disabled={!pdf}
                    label="회전"
                    tooltip="회전(0→90→180→270)"
                />

                <ActionButton
                    variant="download"
                    onClick={() => {
                        if (!path) return;
                        if (typeof path === "string") window.open(path, "_blank", "noopener,noreferrer");
                    }}
                    disabled={!path || typeof path !== "string"}
                    label="다운로드"
                />

                <IconButton size="small" onClick={onClose} sx={{ color: "#64748b" }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {showStrip && (
                <>
                    <Box sx={{ px: 2.5, py: 1.25, bgcolor: "#fafbfc", borderBottom: "1px solid #eef2f6" }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <SearchIcon sx={{ color: "text.secondary" }} />
                            <Typography variant="body2" color="text.secondary">
                                검색어:
                            </Typography>
                            <Chip label={keyword || "없음"} size="small" variant="outlined" sx={{ fontWeight: 900 }} />
                            {!!numPages && (
                                <Typography variant="body2" color="text.secondary">
                                    / 총 {numPages}p
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                    <Divider />
                </>
            )}

            <Box
                ref={viewerRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    bgcolor: "#fff",
                    overflow: "hidden",
                    width: "100%",
                    height: "100%",
                }}
            >
                {!path ? (
                    <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>PDF 경로(path)가 없습니다.</Box>
                ) : (
                    <Document
                        file={path}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={(e) => console.error("PDF onLoadError:", e)}
                        onSourceError={(e) => console.error("PDF onSourceError:", e)}
                        loading={
                            <Box sx={{ p: 4, textAlign: "center" }}>
                                <LoadingDots label={'자료를 불러오고 있어요'} />
                            </Box>
                        }
                        error={<Box sx={{ p: 4, textAlign: "center", color: "error.main", fontWeight: 900 }}>PDF 로딩 오류</Box>}
                        noData={<Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>PDF 파일이 없습니다.</Box>}
                    >
                        {pdf && numPages ? (
                            <List
                                ref={listRef}
                                height={Math.max(240, vh || 600)}
                                itemCount={numPages}
                                itemSize={itemSize}
                                width="100%"
                                overscanCount={2}
                                itemData={itemData}
                                style={{ background: "#fff", overflowX: "hidden", overflowY: "auto" }}
                            >
                                {PdfRow}
                            </List>
                        ) : null}
                    </Document>
                )}
            </Box>

            <Divider />

            <Box
                sx={{
                    px: 2.5,
                    py: 1.5,
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    bgcolor: "#fafbfc",
                    flexShrink: 0,
                }}
            >
                <ActionButton variant="close" onClick={onClose} />
            </Box>
        </Dialog>
    );
}
